import { Node } from '@tiptap/core';

/**
 * Custom Tiptap Node Extension for File Buttons
 * Adheres to Tiptap's strict schema requirements
 */
export const FileButton = Node.create({
  name: 'fileButton',
  
  group: 'block',
  
  // Atom node - no editable content, just displays as a button
  atom: true,
  
  // Allow no marks inside (button text is plain)
  marks: '',
  
  // Define attributes that can be stored
  addAttributes() {
    return {
      url: {
        default: null,
        parseHTML: element => element.getAttribute('href'),
        renderHTML: attributes => {
          if (!attributes.url) {
            return {};
          }
          return {
            href: attributes.url,
            target: '_blank',
          };
        },
      },
      fileName: {
        default: null,
        parseHTML: element => element.textContent,
        renderHTML: attributes => {
          return {};
        },
      },
    };
  },
  
  // Parse HTML - how to recognize this node from pasted HTML
  parseHTML() {
    return [
      {
        tag: 'a[data-file-button="true"]',
        getAttrs: (element) => {
          if (typeof element === 'string') return false;
          return {
            url: element.getAttribute('href'),
            fileName: element.textContent,
          };
        },
      },
      {
        tag: 'a[style*="background-color: #d48c95"]',
        getAttrs: (element) => {
          if (typeof element === 'string') return false;
          return {
            url: element.getAttribute('href'),
            fileName: element.textContent,
          };
        },
      },
    ];
  },
  
  // Render HTML - how to output this node as HTML
  renderHTML({ HTMLAttributes, node }) {
    const fileName = node.attrs.fileName || HTMLAttributes?.fileName || 'Download File';
    const url = node.attrs.url || HTMLAttributes?.url || '#';
    
    // Get file icon based on extension
    const getFileIcon = (filename) => {
      const ext = filename.split('.').pop()?.toLowerCase();
      if (['pdf'].includes(ext)) return '📄';
      if (['doc', 'docx'].includes(ext)) return '📝';
      if (['xls', 'xlsx'].includes(ext)) return '📊';
      if (['zip', 'rar'].includes(ext)) return '📦';
      if (['mp3', 'wav', 'mp4'].includes(ext)) return '🎵';
      return '📎';
    };
    
    // Return ProseMirror DOM structure: [tag, attrs, ...children]
    // For atom nodes, we include the text directly
    const buttonText = `${getFileIcon(fileName)} ${fileName}`;
    
    return [
      'p',
      { style: 'margin: 20px 0; text-align: center;' },
      [
        'a',
        {
          href: url,
          target: '_blank',
          'data-file-button': 'true',
          style: 'display: inline-block; background-color: #d48c95; color: #ffffff; padding: 14px 28px; border-radius: 8px; text-decoration: none; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif; font-size: 16px; font-weight: 600; line-height: 1.5; white-space: nowrap; border: none; cursor: pointer;',
        },
        buttonText,
      ],
    ];
  },
  
  // Add text node as child for the button text
  addNodeView() {
    return ({ node }) => {
      const fileName = node.attrs.fileName || 'Download File';
      const url = node.attrs.url || '#';
      
      const getFileIcon = (filename) => {
        const ext = filename.split('.').pop()?.toLowerCase();
        if (['pdf'].includes(ext)) return '📄';
        if (['doc', 'docx'].includes(ext)) return '📝';
        if (['xls', 'xlsx'].includes(ext)) return '📊';
        if (['zip', 'rar'].includes(ext)) return '📦';
        if (['mp3', 'wav', 'mp4'].includes(ext)) return '🎵';
        return '📎';
      };
      
      const wrapper = document.createElement('p');
      wrapper.style.cssText = 'margin: 20px 0; text-align: center;';
      
      const button = document.createElement('a');
      button.href = url;
      button.target = '_blank';
      button.setAttribute('data-file-button', 'true');
      button.style.cssText = 'display: inline-block; background-color: #d48c95; color: #ffffff; padding: 14px 28px; border-radius: 8px; text-decoration: none; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif; font-size: 16px; font-weight: 600; line-height: 1.5; white-space: nowrap; border: none; cursor: pointer;';
      button.textContent = `${getFileIcon(fileName)} ${fileName}`;
      
      wrapper.appendChild(button);
      
      return {
        dom: wrapper,
        contentDOM: null, // Atom node has no editable content
      };
    };
  },
  
  // Add commands to insert file buttons
  addCommands() {
    return {
      insertFileButton: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});

